import React, { useState, useEffect } from "react";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import Grid from "@mui/material/Grid";
import { Button } from "components/Buttons";
import { ButtonColorScheme, ButtonType } from "types/ui";
import { CircularProgress } from "@material-ui/core";
import { icons } from "constants/icons";
import Box from "@mui/material/Box";
import { Alert, Typography, Divider } from "@mui/material";
import {
  generatePatientReport as gatherReportInfo,
  postPerformanceReport as generatePerformanceReport,
} from "repositories/patientApi";

// Interface for the expected structure of reportInfo
interface ReportInfo {
  message: string;
  s3_bucket: string;
  s3_pdf_key: string;
  report_data: {
    patient_id: string;
    age: number;
    gender: string;
    movement_type: string;
    movement_angle: number;
    generated_report: string;
    related_links: { [key: string]: string };
    previous_data: number;
  };
  internalData: { [key: string]: any };
}

type ReportModalProps = {
  open: boolean;
  onClose: () => void;
  patientName?: string;
  patientId?: string;
  patientReport?: string;
  reportDate?: string;
};

const ReportModal: React.FC<ReportModalProps> = ({
  open,
  onClose,
  patientName,
  patientReport,
  reportDate,
}) => {
  const [error, setError] = useState<string | null>(null);

  const handleClose = () => {
    setError(null);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Grid container justifyContent="space-between" alignItems="center">
          <span>Generate Report for {patientName || "Selected Patient"}</span>
          <img
            src={icons.modalClose}
            alt="close"
            aria-hidden="true"
            className="cursor-pointer"
            onClick={handleClose}
          />
        </Grid>
      </DialogTitle>

      <DialogContent>
        {!patientReport && (
          <div className="p-3 mb-4 border rounded-md border-amber-500 bg-amber-50">
            <p className="font-medium text-amber-700">
              Report Generation Warning
            </p>
            <p className="text-sm text-amber-600">
              No report available for this patient.
            </p>
          </div>
        )}

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}

        {patientReport && (
          <Box
            sx={{
              mt: 3,
              p: 2,
              border: "1px solid #e0e0e0",
              borderRadius: 1,
              maxHeight: "60vh",
              overflowY: "auto",
              backgroundColor: "#f9f9f9",
            }}
          >
            <Box sx={{ mb: 3 }}>
              <Typography
                variant="h6"
                gutterBottom
                sx={{ fontWeight: "bold", color: "#1976d2" }}
              >
                LLM SUMMARIZATION OUTPUT
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Box
                sx={{
                  p: 2,
                  backgroundColor: "white",
                  borderRadius: 1,
                  border: "1px solid #eee",
                }}
              >
                <pre
                  style={{
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                    fontFamily: "inherit",
                    fontSize: "0.9rem",
                  }}
                >
                  {patientReport || "No summary available."}
                </pre>
              </Box>
            </Box>
          </Box>
        )}
      </DialogContent>

      <DialogActions
        sx={{ padding: "16px 24px", borderTop: "1px solid #e0e0e0" }}
      >
        <Button
          title="Close"
          onClick={handleClose}
          type={ButtonType.normal}
          colorScheme={ButtonColorScheme.default}
        />
      </DialogActions>
    </Dialog>
  );
};

export default ReportModal;
